import { usePublishActionOutcome } from "../ui/ActionConfirmation.js";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getBookingSetup } from "../api.js";
import { idempotencyKey } from "../booking/api.js";
import {
  PRIVATE_BOOKING_SETUP_OPERATION,
  isProblem,
  linksToSchoolPayers,
  payerLabel,
  requestPrivateBookingSetup,
  setupCopy,
} from "../booking/payerSetup.js";
import { canManageSchoolDomain, useAuth } from "../auth.js";
import { useAsync } from "../useAsync.js";

/**
 * Who pays for this student, and whether private booking is set up.
 *
 * The staff-facing idea is a **booking payer** — the person a lesson is booked
 * and billed against. Usually a parent, sometimes the student, and shared
 * between siblings, because one payer is one customer in the booking system
 * however many children they book for. Nothing here says "Amelia customer" or
 * shows an id: an owner setting a new child up should not have to know the
 * booking system exists, let alone hold one of its record numbers.
 *
 * The state is read from the Hub, which is authoritative for it, and drawn.
 * After a successful setup the record is re-read rather than assumed — the
 * link is written by the booking system calling back into the Hub, so the Hub
 * saying so is the only confirmation worth showing.
 */
export function BookingPayer({
  studentId,
  correction,
}: {
  studentId: string;
  /**
   * Changes whenever the student's record is corrected, so the projection is
   * re-read. Without it, fixing the payer email leaves the panel still saying
   * the details are missing until somebody reloads the page — which is the
   * exact loop this screen exists to close.
   */
  correction: number;
}) {
  const publish = usePublishActionOutcome();
  const { me } = useAuth();
  const { data, error, loading, reload } = useAsync(
    () => getBookingSetup(studentId),
    [studentId, correction],
  );
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * One key for one student's setup, held across every press.
   *
   * Held **including after a failure**, which is the part worth stating: the
   * case the key exists for is the booking system completing the work and the
   * reply not arriving. That is indistinguishable here from the work never
   * happening, so the next press must carry the same key and let the far side
   * say "already done" rather than resolve the payer again. The backend
   * explicitly allows a failed setup to be retried on the same key once the
   * payer details are corrected, so reminting buys nothing and loses that.
   */
  const [retryKey, setRetryKey] = useState(idempotencyKey);

  /**
   * A different student is a different attempt, and reusing a key across two
   * of them is refused by the booking system as a conflict.
   *
   * Reset during render rather than in an effect, and self-contained rather
   * than relying on the caller passing a `key` prop: the route can hand this
   * component a new `studentId` without remounting it, and a component whose
   * correctness depends on somebody else remembering to remount it is one
   * that will eventually be rendered without that.
   */
  const [keyedTo, setKeyedTo] = useState(studentId);
  if (keyedTo !== studentId) {
    setKeyedTo(studentId);
    setRetryKey(idempotencyKey());
    setProblem(null);
  }

  if (loading || error || !data) {
    return (
      <div className="panel">
        <h2 className="small" style={{ margin: "0 0 4px" }}>
          Booking payer
        </h2>
        {loading && <p className="empty">Loading…</p>}
        {error && <div className="error small">{error}</div>}
      </div>
    );
  }

  const copy = setupCopy(data);

  async function setUp() {
    setWorking(true);
    setProblem(null);
    try {
      await requestPrivateBookingSetup(studentId, retryKey);
      publish({ variant: "success", title: "Booking setup request completed", details: [{ value: "Review the refreshed booking-payer status before booking a lesson." }] });
      // Re-read rather than patch the state we are holding: what makes a
      // student ready is the link recorded on this side, and only re-reading
      // shows that it actually arrived.
      reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not set them up.");
      // The key is deliberately *not* reminted here. A thrown request does not
      // mean nothing happened — the most dangerous case is the far side having
      // completed and the reply having been lost — so the next press carries
      // the same key and is answered as the retry it is.
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="panel">
      <h2 className="small" style={{ margin: "0 0 4px" }}>
        Booking payer
      </h2>

      {problem && <div className="error small">{problem}</div>}

      <table>
        <tbody>
          <tr>
            <td className="muted small">Payer</td>
            <td>
              {data.payer === null ? (
                <span className="muted">{payerLabel(data)}</span>
              ) : (
                payerLabel(data)
              )}
            </td>
          </tr>
          <tr>
            <td className="muted small">Private booking</td>
            <td>
              {isProblem(data.state) ? <span className="tag warn">{copy.status}</span> : copy.status}
            </td>
          </tr>
        </tbody>
      </table>

      <p className="small muted" style={{ marginTop: 8 }}>
        {copy.detail}
        {canManageSchoolDomain(me) && linksToSchoolPayers(data) && (
          <>
            {" "}
            <Link to="/school-payers#school-funded-settings">School-funded payer settings</Link>
          </>
        )}
      </p>

      {copy.action && (
        <div className="row" style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={setUp}
            disabled={working || PRIVATE_BOOKING_SETUP_OPERATION === null}
          >
            {working ? "Setting up…" : "Set up private booking"}
          </button>
          {PRIVATE_BOOKING_SETUP_OPERATION === null && (
            /* Shown rather than hidden. An owner who has just filled a record
               in correctly needs to know the record is right and the wait is
               somewhere else — a button that quietly vanishes reads as the
               student still being wrong. */
            <span className="small muted">
              Waiting on the booking system to publish this operation. Nothing about this
              student&rsquo;s record needs changing; their first private lesson booking will set
              the payer up in any case.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
